import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    BehaviorSubject,
    catchError,
    filter,
    map,
    mergeMap,
    Observable,
    of,
    switchMap,
    take,
    throwError,
    timeout,
} from 'rxjs';

import { EcmDispatcherModule, IdZoneService } from '@ecm/dispatcher';

import { Contract } from '../business/api/contracts/contract/contract';
import { ContractsBusinessModel } from '../business/api/contracts/contracts.business-model';
import { Brand } from '../business/context/brand.enum';
import { UrlHelper } from '../utils/url-helper';
import { PortfolioService } from './portfolio.service';

@Injectable({
    providedIn: 'root',
})
export class ContextService {
    /**
     * Class name for logger
     * @type {string}
     */
    private static readonly CLASS_NAME = 'ContextService';

    private currentBrand: Brand;

    private readonly _contextId$ = new BehaviorSubject<string>('');

    protected readonly platformId = inject(PLATFORM_ID);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly portfolioService = inject(PortfolioService);
    private readonly router = inject(Router);
    private readonly idZoneService = inject(IdZoneService);

    // public ctxId = toSignal(this.contextId$);
    // Contract ID regex
    public static readonly CIDRegexp = /^\d{10}$/; // restricting the CID match to 10 numbers only
    // Member ID regex
    public static readonly MIDRegexp = /^[1-9]\d{17}\d?$/; // restricting the MID match to 18 numbers only

    public get contextId$(): Observable<string> {
        return this._contextId$.asObservable();
    }

    public updateContextId(contextId: string): void {
        this._contextId$.next(contextId);
    }

    /**
     * Is user authenticated
     * @returns {Observable<boolean>}
     */
    public isAuthenticated(): Observable<boolean> {
        if (!isPlatformBrowser(this.platformId)) {
            return of(false);
        }
        // Wait for the async IDZone data before evaluating the ident level.
        // IDZone can only CONFIRM authentication (fast path, no HTTP call):
        // a non-FULL ident level is not authoritative — it may be the default
        // (visitor) payload emitted at the IDZone 3s timeout, or a degraded
        // IDZone context. Denial is always confirmed by the HTTP call.
        return this.idZoneService.whenLoaded$.pipe(
            switchMap(() => {
                if (this.idZoneService.userIdentLevel()?.toUpperCase() === 'FULL') {
                    return of(true);
                }
                return this.isAuthenticatedFromLoggedContract();
            }),
            // If IDZone never loads at all, don't block: after 2s, fall back
            // to the logged contract HTTP call to evaluate authentication.
            timeout({
                first: 2000,
                with: () => this.isAuthenticatedFromLoggedContract(),
            }),
        );
    }

    /**
     * Authentication fallback based on the logged contract HTTP call.
     * @returns {Observable<boolean>}
     */
    private isAuthenticatedFromLoggedContract(): Observable<boolean> {
        return this.portfolioService.getLoggedContract().pipe(
            map(() => true),
            catchError(() => of(false)),
        );
    }

    /**
     * ⚠️ DEPRECATED - Is current brand Sosh
     *
     * @deprecated Use {@link PortfolioFlagsService#isSosh} Signal from @ecm/portfolio instead.
     * The new pattern provides:
     * - Centralized brand detection (single source of truth)
     * - Angular Signals for reactive updates
     * - Better testability and maintainability
     *
     * Migration example:
     * ```typescript
     * // ❌ OLD (DEPRECATED):
     * this.contextService.isSosh().subscribe(isSosh => { ... });
     *
     * // ✅ NEW (RECOMMENDED):
     * private readonly portfolioFlagsService = inject(PortfolioFlagsService);
     * public isSosh = this.portfolioFlagsService.isSosh;
     * if (this.isSosh()) { ... }
     * ```
     *
     * @returns {Observable<boolean>}
     */
    public isSosh(): Observable<boolean> {
        return this.getBrand().pipe(
            take(1),
            map((brand: Brand) => {
                this.currentBrand = brand;
                const isSosh: boolean = brand === Brand.SOSH;
                EcmDispatcherModule.sendConsoleInfo(ContextService.CLASS_NAME, 'User brand is ' + brand);
                return isSosh;
            }),
        );
    }

    private checkUserPortfolioBrandsAndType(contractsBusinessModel: ContractsBusinessModel) {
        let hasSosh = false;
        let hasOrange = false;
        let hasVimEligible = false;
        contractsBusinessModel.contracts.forEach((contract: Contract) => {
            const brand: string = contract.brand.toUpperCase();
            if (brand === Brand.SOSH) {
                hasSosh = true;
            } else {
                hasOrange = true;
            }
            if (contract.telco?.state?.toUpperCase() === 'VIM_ELIGIBLE') {
                hasVimEligible = true;
            }
        });
        return {
            hasVimEligible,
            isFullSosh: hasSosh && !hasOrange,
            isFullOrange: hasOrange && !hasSosh,
        };
    }

    /**
     * Brand calculator
     * based on user Portfolio and current route
     * @returns {Observable<Brand>}
     */
    private getBrand(): Observable<Brand> {
        if (!isPlatformBrowser(this.platformId)) {
            return of(Brand.ORANGE);
        }
        return this.portfolioService.getRawContracts().pipe(
            filter(() => {
                return this.router.routerState.snapshot.url !== '';
            }),
            mergeMap((contractsBusinessModel: ContractsBusinessModel) => {
                const { hasVimEligible, isFullSosh, isFullOrange } =
                    this.checkUserPortfolioBrandsAndType(contractsBusinessModel);

                // top-level cases
                if (isFullSosh) {
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is full Sosh');
                    return of(Brand.SOSH);
                } else if (isFullOrange) {
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is full Orange');
                    return of(Brand.ORANGE);
                } else if (hasVimEligible) {
                    // if user has at least 1 `VIM_ELIGIBLE` contract, always return logged contract brand
                    return this.portfolioService.getLoggedContract().pipe(
                        map((loggedContract: Contract) => {
                            return loggedContract.brand.toUpperCase() === Brand.SOSH ? Brand.SOSH : Brand.ORANGE;
                        }),
                        catchError((e) => {
                            return throwError(() => e);
                        }),
                    );
                } else {
                    return this.handleMixedBrandsCase();
                }
            }),
            catchError(() => {
                // in case of error, return brand Orange by default
                EcmDispatcherModule.sendConsoleDebug(
                    ContextService.CLASS_NAME,
                    'Error on portfolio request (/contracts)',
                );
                return of(Brand.ORANGE);
            }),
        );
    }

    private handleMixedBrandsCase() {
        // mixed brands case
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'User is mixed Orange / Sosh');
        // check if cid present in current route
        const href: string = globalThis.location.href;
        const stateUrl: string = this.router.routerState.snapshot.url;
        const cidMatch: string[] = UrlHelper.matchCidInPath(stateUrl);
        const midMatch: string[] = UrlHelper.matchMidInPath(stateUrl);
        // Regex to match "carrousel" query params value in URL
        const matchCarrouselQueryParamsValue = /[?&]carrousel=([^&]+)/;

        // Get carrousel query params value in url
        const carrouselValueInUrl = RegExp(matchCarrouselQueryParamsValue).exec(stateUrl);

        if (cidMatch) {
            const cid = cidMatch[1];
            return this.handleMixedBrandsCaseWithMatchedCid(cid);
        } else if (midMatch) {
            const mid = midMatch[1];
            return this.handleMixedBrandsCaseWithMatchedMid(mid);
        } else if (carrouselValueInUrl) {
            const encodedCidOrMid = carrouselValueInUrl[1];
            // decode URL encoded cid
            const urlDecoded = decodeURIComponent(encodedCidOrMid);
            const decodedCidOrMid = atob(urlDecoded);
            if (RegExp(ContextService.CIDRegexp).exec(decodedCidOrMid)) {
                return this.handleMixedBrandsCaseWithMatchedCid(decodedCidOrMid);
            } else if (RegExp(ContextService.MIDRegexp).exec(decodedCidOrMid)) {
                return this.handleMixedBrandsCaseWithMatchedMid(decodedCidOrMid);
            }
        }
        // current route has no cid (transversal route)
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'Transversal route');
        // check `sosh` or `orange` query param presence
        EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'Will check query params');

        return this.activatedRoute.queryParams.pipe(
            filter((params: Params) => {
                /**
                 * FGO : Hacky test to filter Angular Router initialization
                 *   with empty queryParams (even if query params set in URL...)
                 *   See https://github.com/angular/angular/issues/12157
                 */
                return Object.keys(params).length > 0 === (href.indexOf('?') !== -1 || stateUrl.indexOf('?') !== -1);
            }),
            map((params: Params) => {
                const lowerCaseParams: Params = {};
                for (const key of Object.keys(params)) {
                    lowerCaseParams[key.toLowerCase()] = params[key];
                }
                return lowerCaseParams;
            }),
            map((params: Params) => {
                if (Object.hasOwn(params, 'sosh')) {
                    return Brand.SOSH;
                } else if (Object.hasOwn(params, 'orange')) {
                    return Brand.ORANGE;
                } else if (!this.currentBrand) {
                    // default case : brand Orange
                    EcmDispatcherModule.sendConsoleDebug(ContextService.CLASS_NAME, 'No query param for brand');
                    return Brand.ORANGE;
                }
                // no query param and brand is already set, just keep using it
                EcmDispatcherModule.sendConsoleDebug(
                    ContextService.CLASS_NAME,
                    'No query param and brand is already set : ' + this.currentBrand + ' => Will stay on this brand',
                );
                return this.currentBrand;
            }),
        );
    }

    private handleMixedBrandsCaseWithMatchedCid(cid: string) {
        EcmDispatcherModule.sendConsoleDebug(
            ContextService.CLASS_NAME,
            'Contract route, will handle brand by cid (ContractId)',
        );
        // current route has a cid, set brand according to cid
        return this.portfolioService.getContractByCid(cid).pipe(
            map((contract: Contract) => {
                // cid belongs to portfolio
                if (contract?.brand?.toLowerCase() === Brand.SOSH.toLowerCase()) {
                    return Brand.SOSH;
                }
                // cid doesn't belong to portfolio
                return Brand.ORANGE;
            }),
            catchError((e) => {
                return throwError(() => e);
            }),
        );
    }

    private handleMixedBrandsCaseWithMatchedMid(mid: string) {
        EcmDispatcherModule.sendConsoleDebug(
            ContextService.CLASS_NAME,
            'Contract route, will handle brand by mid (MemberId)',
        );
        return this.portfolioService.getRawContracts().pipe(
            switchMap((contract: ContractsBusinessModel) => {
                const currentCid: any = contract?.contracts?.find((contract: Contract) => {
                    return contract?.associatedFamilyMembers?.some((member) => member.id === mid);
                }).cid;
                return this.handleMixedBrandsCaseWithMatchedCid(currentCid);
            }),
        );
    }
}
